import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";

import { Link } from "react-router-dom";

function Lightning93() {
  const contentRef = useRef(null);
  const titleText = "Understanding Lightning Channels";
  const questions = [
    {
      text: "1. What is the main purpose of a Lightning channel in Bitcoin??",
      options: [
        {
          value: "A",
          label: "A. To create a new cryptocurrency separate from Bitcoin",
        },
        {
          value: "B",
          label:
            "B. To allow fast, low-fee transactions off-chain while settling on-chain only when necessary",
        },
        {
          value: "C",
          label: "C. To replace the Bitcoin blockchain entirely",
        },
        {
          value: "D",
          label: "D. To store private keys for multiple users",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What ensures that neither participant can cheat by broadcasting an old state of the channel?",
      options: [
        {
          value: "A",
          label: "A. Blockchain mining rewards",
        },
        {
          value: "B",
          label: "B. Commitment transactions and revocation secrets",
        },
        {
          value: "C",
          label: "C. Lightning network fees",
        },
        {
          value: "D",
          label: "D. Node IP addresses",
        },
      ],
      correct: "B",
    },
    {
      text: "3. Which of the following correctly describes BOLT 11 and BOLT 12 invoices ?",
      options: [
        {
          value: "A",
          label: "A. Both are reusable and offer the same level of privacy",
        },
        {
          value: "B",
          label: "B. BOLT 11 invoices are reusable, BOLT 12 invoices expire",
        },
        {
          value: "C",
          label:
            "C. BOLT 11 invoices are one-time use, while BOLT 12 invoices are reusable and still maintain privacy",
        },
        {
          value: "D",
          label: "D. BOLT 12 invoices require an external server to function",
        },
      ],
      correct: "C",
    },
    {
      text: "4. After receiving funds, how does a user send sats on the Lightning network?",
      options: [
        { value: "A", label: "A. Enter the receiver’s seed phrase" },
        {
          value: "B",
          label:
            "B. Paste or scan a Lightning invoice and approve the transaction",
        },
        {
          value: "C",
          label: "C. Convert Lightning sats into on-chain coins first",
        },
        {
          value: "D",
          label: "D. Open a new Lightning channel manually",
        },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={93}
        timerMinutes={60}
        currentLevel={7}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 93</b>! Today we begin exploring one of the most
        important ideas in the Lightning Network: the concept of Lightning
        channels. These channels are the backbone of how fast, low-fee Bitcoin
        payments become possible. Instead of broadcasting every small
        transaction to the blockchain, two people can open a private channel,
        update their balances as many times as they want, and only settle
        on-chain when they close it. This simple idea unlocks speed, efficiency,
        and scalability while preserving Bitcoin’s trustless nature. In this
        lesson, we will walk through how channels are created, how balances are
        updated without relying on trust, and how the network routes payments
        across multiple participants. By the end, you will have a clear
        understanding of what channels are, why they matter, and how they make
        everyday Bitcoin transactions instantaneous.
        <br />
        <br />
        <b>How the Lightning Network Works </b>
        <br />
        <br />
        The Lightning Network is often described as a second layer built on top
        of Bitcoin, but to truly understand how it works you first need a clear
        picture of what problem it is trying to solve. Bitcoin, as you already
        know, is an extraordinary invention because it allows people anywhere in
        the world to send digital money without relying on banks or any central
        authority. Every transaction is verified by thousands of computers and
        stored permanently on the blockchain. This gives Bitcoin its security
        and neutrality, but it also comes with a natural limitation. Every
        transaction has to be included in a block, and blocks on Bitcoin are
        created around every ten minutes. This means that if the entire world
        tried to send payments directly on the base layer, the system would
        become congested, slow and extremely expensive as we discussed in the
        previous chapters. It was never designed to process every single cup of
        coffee, bus ticket or micro-payment on the planet.
        <br />
        This is where the Lightning Network enters the picture. Instead of
        forcing every transaction onto the blockchain, Lightning creates a
        different way for people to transact instantly, privately and with
        extremely low fees while still relying on Bitcoin’s security. It is a
        clever design because it keeps the heavy, irreversible settlement on the
        Bitcoin blockchain, while allowing day-to-day activity to happen
        off-chain between users. To make this easier to understand, imagine two
        users, Amina and Bilal, who often exchange small amounts of money. If
        both of them used only the base layer of Bitcoin, every small
        transaction would need to be confirmed by miners and recorded forever in
        the blockchain. It would work, but it would be slow and expensive, and
        it would unnecessarily fill the global ledger with tiny payments.
        <br />
        Lightning offers a simple alternative. Instead of sending every payment
        to the blockchain, Amina and Bilal can create what is known as a payment
        channel. You can think of a payment channel like a private tab between
        two people. They both lock some bitcoin into a shared arrangement, and
        as long as the channel is open, they can send payments back and forth
        instantly, without waiting for miners or paying high fees. Only the
        initial setup and final closing of the channel touches the blockchain.
        Everything else happens off-chain, but in a way that is still
        cryptographically enforced so neither person can cheat.
        <br />
        To understand this more clearly, imagine Amina and Bilal decide to open
        a channel between them. Amina puts in a certain amount of bitcoin. Let
        us say she contributes a small sum for daily spending. Bilal does not
        necessarily need to add anything, although he can if he wants to. Once
        the channel is open, they can update the balance between them whenever
        they wish. If Amina wants to send some sats to Bilal, the wallet simply
        updates their balance inside the channel. The channel acts like a
        constantly updating receipt of who owns what. None of this is broadcast
        to the blockchain, so it is instant. This is why Lightning is so fast.
        It avoids the bottleneck of global consensus and instead relies on a
        cryptographic contract between users.
        <br />
      </p>
      <img src="lightning_93_1.png"></img>
      <p className="content">
        But Lightning becomes truly powerful not just because two people can
        update their balance privately. Its real strength comes from the fact
        that anyone can connect to anyone else through a network of channels.
        This creates a web of many interconnected payment channels, allowing
        payments to flow from one person to another even if they do not have a
        direct channel between them. This is like the banking system but without
        a bank. Imagine Amina has a channel with Bilal, and Bilal has another
        channel with a third person named Careem. Amina can send money to Careem
        even if she does not have any direct channel with him. The payment
        simply follows the path Amina → Bilal → Careem. This entire process
        happens automatically inside the Lightning Network, and the user does
        not need to understand the routing behind the scenes. The wallet handles
        it.
        <br />
        This routing mechanism is one of the reasons Lightning scales so well.
        Instead of millions of people each opening channels with every person
        they want to transact with, they can simply connect to a few nodes, and
        the network will find a path for payments. You can think of it like the
        internet. When you open a webpage, your data does not travel in a
        straight line to the server. It hops through different routers across
        the world until it eventually reaches its destination. Lightning
        payments behave similarly. The network finds a path through existing
        channels to route your payment securely and instantly.
        <br />
        One important feature of Lightning is that neither the sender nor the
        intermediary in the path needs to trust each other. The system uses a
        clever cryptographic construction so that each hop in the path is
        guaranteed to behave correctly. This means that Bilal cannot steal
        Amina’s money when she sends it to Careem through him, nor can Careem
        wrongly claim funds that were not intended for him. Every part of the
        payment is protected by what is known as a Hash Time Locked Contract, or
        HTLC. You do not need to understand the mathematics behind it right now,
        but the basic idea is that funds are only released if certain
        cryptographic conditions are met, and they automatically return to the
        sender if a payment fails. This keeps the network safe even though
        payments may pass through many strangers.
        <br />
        You may now wonder why anyone would want to act as a routing node like
        Bilal in this example. The answer is that routing nodes earn small fees
        for forwarding payments. These fees are tiny, often fractions of a
        satoshi, but over time they can accumulate. This creates an incentive
        for individuals or businesses to maintain channels and provide liquidity
        to the network. Lightning therefore becomes an ecosystem where users,
        wallets, and routing nodes all play important roles. Users benefit from
        fast and cheap payments, while routing nodes are rewarded for keeping
        the network connected.
        <br />
        <br />
      </p>
      <img src="lightning_93_2.jpeg"></img>
      <p className="content">
        Another important aspect of Lightning is liquidity. When Amina opens a
        channel with Bilal and deposits bitcoin into it, those funds are locked
        into the channel. Amina can send sats to Bilal, but she cannot send more
        than what she has allocated to that channel. This means the direction of
        liquidity matters. If a user wants to receive payments, they must have
        inbound liquidity. If they want to send payments, they need outbound
        liquidity. Good wallets help manage this automatically, so users never
        have to think about it. But behind the scenes, liquidity is the fuel
        that makes the network function. When a payment is routed across
        multiple channels, the network must ensure that each channel in the path
        has enough liquidity in the correct direction.
        <br />
        As you continue learning more about how the Lightning Network works, the
        important point to keep in mind is that it is built on top of Bitcoin
        and relies fully on Bitcoin’s security. When Amina and Bilal open a
        channel, they create a type of contract enforced by the blockchain. If
        one party tries to cheat by broadcasting an old state of the channel,
        the protocol is designed to punish the cheating party by giving all the
        funds to the honest participant. This is another example of how game
        theory ensures correct behavior without requiring trust.
        <br />
        At this stage, you have a broad understanding of the motivation behind
        the Lightning Network, how payment channels work at a basic level, and
        how routed payments allow anyone to pay anyone else across the network.
        In the next part, we will dive more deeply into what happens inside a
        channel when balances change, how HTLCs work in a simplified manner, and
        how the network remains secure even when people are offline.
        <br />
        <br />
        <b>Inside a Lightning Channel</b>
        <br />
        <br />
        As we continue exploring how the Lightning Network actually functions,
        it becomes important to understand that Lightning is not a separate
        blockchain and does not replace Bitcoin’s base layer. Instead, it builds
        on what Bitcoin already offers while reducing the load on the main
        chain. In Part 1, we saw how Amina and Bilal created a channel together
        using a funding transaction. Now it is time to understand more deeply
        what happens inside that channel and how Bitcoin’s rules help enforce
        fairness and security between both parties without requiring trust.
        <br />
        Imagine that Amina and Bilal have just created their channel. The
        initial funding transaction is sitting on the blockchain as a two-of-two
        multisig output. This output is special because neither Amina nor Bilal
        can move the funds by themselves. They must both agree. What gives
        Lightning its power is the idea that, before a new update to their
        balances is ever broadcast, the two participants privately exchange
        commitments that represent the latest “state” of their relationship.
        These states never have to touch the blockchain unless something goes
        wrong. If everything runs smoothly, the blockchain sees only the very
        first funding transaction and the final settlement when the channel is
        closed.
        <br />
        To see how this works, let us return to Amina and Bilal’s example.
        Suppose they opened the channel with 10000 satoshis, with Amina
        contributing the entire amount. Even though both control the channel
        jointly, Lightning keeps track of who owns how much by continuously
        updating a balance sheet off-chain. At the start, Amina owns all 10000
        sats and Bilal owns 0. They each hold a version of a commitment
        transaction that reflects this state. The commitment transaction is a
        pre-signed transaction showing how funds would be distributed if the
        channel were closed at that moment. Even though this transaction is not
        broadcast, it is fully valid and could be broadcast if necessary.
        <br />
        When Amina wants to send 1000 sats to Bilal, they update their “balance
        sheet.” But they do something extremely clever: instead of modifying a
        file or database, they create a new pair of commitment transactions that
        reflect the new balance i.e Amina now owns 9000 sats and Bilal owns 1000
        sats. They each sign the other’s commitment transaction. At the same
        time, they revoke the previous version. Revocation is a central part of
        Lightning, because it ensures that neither party has an incentive to
        cheat by broadcasting an old state.
        <br />
        Revocation works like this: each commitment transaction contains a
        hidden key or secret known only to one party. When both participants
        agree on a new state, they reveal the old secret to the other. Revealing
        this secret means that if someone attempts to broadcast an older
        commitment transaction, that old state becomes punishable. The other
        party can immediately claim all the funds from the channel. This
        eliminates the incentive for either Amina or Bilal to cheat. The
        punishment is so severe that honesty becomes the rational strategy,
        which is a perfect example of game theory applied to money.
        <br />
        Let us illustrate this. Imagine Amina tries to cheat by broadcasting the
        old commitment transaction showing she still owns 10000 sats. Since she
        already revealed the secret linked to that version when they moved to
        the updated balance, Bilal could take the old transaction, use the
        revealed secret, and sweep all 10000 sats for himself. In other words,
        Amina would lose everything if she tried to use an outdated state. This
        mechanism is what makes Lightning “trust-minimized.” You do not have to
        blindly trust the other party, because the protocol gives both of you
        extremely strong incentives to behave fairly.
        <br />
        Now imagine Amina and Bilal exchange money within their channel several
        times. They may go from Amina–9000 / Bilal–1000 to Amina–8500 /
        Bilal–1500, then to Amina–8700 / Bilal–1300 each time creating new
        commitment transactions and revoking old ones. These updates happen
        quickly and privately. None of this traffic touches the blockchain. Only
        when one of them wants to close the channel do they broadcast the most
        recent, valid commitment transaction.
        <br />
        But Lightning’s usefulness goes far beyond two people. The real magic
        happens when payments are routed across multiple channels. To explain
        this, we introduce a new participant: Careem. Suppose Careem has a
        channel with Bilal, and Amina wants to pay Careem. Even if Amina does
        not have a direct channel with Careem, she can still pay him if a
        payment route exists through shared connections. The Lightning Network
        functions much like the internet in this way: your message (or payment)
        travels through nodes until it reaches its destination.
        <br />
        To make routing safe, Lightning uses a concept called a Hashed Timelock
        Contract, or HTLC. This might sound intimidating, but it is actually a
        very elegant idea. Let us walk through it step by step using our
        characters.
        <br />
        Imagine Careem wants to receive 5000 sats from Amina. He generates a
        secret number (called a preimage), hashes it, and gives the hash to
        Amina. The preimage is like the solution to a puzzle, and the hash is
        the puzzle itself. Amina cannot see the solution yet, only the puzzle.
        With the hash in hand, she prepares a conditional payment to Bilal. She
        tells him, “Here are 5000 sats, but you can only claim them if you can
        provide the secret that matches this hash.” Bilal does not yet know the
        secret, so he cannot claim the funds. But he can forward the same
        conditional payment to Careem with a slightly shorter deadline. The
        timelock part simply means each step in the route has a time limit. If
        something fails, the payment reverses automatically.
        <br />
        When the conditional payment reaches Careem, he has the secret (the
        preimage). He reveals it to claim his 5000 sats. The moment he reveals
        it, the secret travels backward through Bilal to Amina. Bilal uses it to
        claim his incoming payment from Amina, and so on. Everyone gets paid
        fairly and securely, and the payment succeeds without Amina ever having
        to know who Careem’s channel partners are. Only the minimum necessary
        information travels through each hop, preserving privacy and reducing
        trust requirements.
        <br />
        <br />
      </p>
      <img src="lightning_93_3.jpeg"></img>
      <img src="lightning_93_4.jpeg"></img>
      <p className="content">
        This system ensures safety at every stage. If at any point someone stops
        cooperating, the payment simply times out and returns to the sender
        without any loss of funds. The HTLC system ensures no one can cheat: you
        only get your incoming payment if you successfully forward the payment
        to the next person.
        <br />
        Routing also introduces the concept of fees. Nodes charge tiny
        forwarding fees when routing payments. This incentivizes individuals to
        open channels and provide liquidity. While fees exist, the amounts are
        incredibly small, iallowing the Lightning Network to remain economical
        even for micro-payments.
        <br />
        As we continue deeper into the mechanics of Lightning, it becomes clear
        that the protocol relies on a combination of Bitcoin’s script system,
        cryptographic secrets, timelocks and game-theoretic incentives. All of
        these elements work together to create a payment system that is fast,
        efficient and secure without requiring trust in any central party.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today we learned how Lightning channels form the foundation of fast and
        scalable Bitcoin payments. We explored how two participants can open a
        channel, fund it, and update balances privately off-chain without
        trusting each other. We saw how commitment transactions and revocation
        secrets ensure security and prevent cheating, and how multi-hop routing
        allows payments to travel across the network even when a direct channel
        does not exist. We also examined how Hashed Timelock Contracts (HTLCs)
        make these payments safe, atomic, and privacy-preserving. Overall, we
        learned how Lightning channels enable instant, low-fee transactions
        while staying fully anchored to Bitcoin’s base layer.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <div className="button-container">
        <Link to="/lightning-92">
          <button className="page-button">Go Back</button>
        </Link>
        <Link to="/index-lightning">
          <button className="page-button">Index</button>
        </Link>
        <Link to="/lightning-94">
          <button className="page-button">Continue</button>
        </Link>
      </div>
    </div>
  );
}

export default Lightning93;
